import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { SchoolCollection, ZoneCollection, ZoneFeature, SchoolFeature, FilterState } from '../types';
import { loadAllData } from '../utils/dataLoader';
import { calculateBounds, getFitBoundsOptions, findSchoolAssignment, getAssignedSchoolCoordinates } from '../utils/mapUtils';
import { calculateMultipleRoutes, estimateDrivingTime } from '../utils/routing';
import { reverseGeocode, formatAddress } from '../utils/geocoding';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create search marker icon
const searchIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Create location pin icon for assignment lookup
const locationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Default fallback center (will be replaced by auto-fit bounds)
const FALLBACK_CENTER: [number, number] = [33.1507, -96.8235];
const FALLBACK_ZOOM = 12;

interface MapComponentProps {
  onLocationClick?: (lat: number, lng: number) => void;
  filters: FilterState;
  searchMarker?: {lat: number, lng: number, address: string} | null;
  onSchoolCountUpdate: (counts: {elementary: number, middle: number, high: number, total: number}) => void;
  onClearSearchMarker: () => void;
  onAssignmentUpdate?: (assignment: {
    lat: number;
    lng: number;
    address?: string;
    elementary?: string;
    middle?: string;
    high?: string;
    drivingTimes?: Array<{
      schoolName: string;
      durationText: string;
      distanceText: string;
      isEstimate?: boolean;
    }>;
  } | null) => void;
}



function MapController({ 
  onLocationClick, 
  mapRef,
  zones,
  schools,
  searchMarker,
  onClearSearchMarker,
  onSetFocusedAssignment,
  onAssignmentUpdate,
  setOutOfDistrictMessage,
  setAssignmentPin
}: { 
  onLocationClick?: (lat: number, lng: number) => void;
  mapRef: React.MutableRefObject<L.Map | null>;
  zones: ZoneCollection | null;
  schools: SchoolCollection | null;
  searchMarker?: {lat: number, lng: number, address: string} | null;
  onClearSearchMarker: () => void;
  onSetFocusedAssignment: (assignment: {elementary?: string; middle?: string; high?: string;} | null) => void;
  onAssignmentUpdate?: (assignment: {lat: number; lng: number; address?: string; elementary?: string; middle?: string; high?: string; drivingTimes?: Array<{schoolName: string; durationText: string; distanceText: string; isEstimate?: boolean;}>;} | null) => void;
  setOutOfDistrictMessage: (message: {lat: number; lng: number; address?: string} | null) => void;
  setAssignmentPin: (pin: {lat: number; lng: number} | null) => void;
}) {
  // Function to calculate driving times for assigned schools
  const calculateDrivingTimes = async (lat: number, lng: number, assignment: any) => {
    if (!schools) return [];
    
    try {
      // Get coordinates for assigned schools
      const schoolCoords = getAssignedSchoolCoordinates(assignment, schools);
      
      if (schoolCoords.length === 0) return [];
      
      // Calculate routes to all assigned schools
      const routes = await calculateMultipleRoutes(lat, lng, schoolCoords);
      
      // Format results
      const drivingTimes = routes.map(({ name, route }) => {
        if (route) {
          return {
            schoolName: name,
            durationText: route.durationText,
            distanceText: route.distanceText,
            isEstimate: false
          };
        } else {
          // Fallback to estimation if routing fails
          const schoolCoord = schoolCoords.find(s => s.name === name);
          if (schoolCoord) {
            const estimate = estimateDrivingTime(lat, lng, schoolCoord.lat, schoolCoord.lng);
            return {
              schoolName: name,
              durationText: estimate.durationText,
              distanceText: estimate.distanceText,
              isEstimate: true
            };
          }
          return null;
        }
              }).filter((item): item is {schoolName: string; durationText: string; distanceText: string; isEstimate: boolean} => item !== null);
      
      return drivingTimes;
    } catch (error) {
      console.error('Error calculating driving times:', error);
      return [];
    }
  };

  const map = useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      console.log('Map clicked at:', lat, lng);
      
      // Clear search marker when clicking elsewhere
      onClearSearchMarker();
      
      // Perform reverse geocoding to get the address
      let address: string | undefined;
      try {
        const reverseResult = await reverseGeocode(lat, lng);
        if (reverseResult) {
          address = formatAddress(reverseResult);
          console.log('Reverse geocoded address:', address);
        }
      } catch (error) {
        console.warn('Reverse geocoding failed:', error);
      }
      
      // Find school assignment for clicked location
      if (zones) {
        const assignment = findSchoolAssignment(lat, lng, zones);
        
        // Check if location is in Frisco ISD
        const hasAnyZone = assignment.zones.elementary || assignment.zones.middle || assignment.zones.high;
        
        if (!hasAnyZone) {
          // Location is not in Frisco ISD
          console.log('Location not in Frisco ISD');
          setOutOfDistrictMessage({ lat, lng, address });
          onSetFocusedAssignment(null); // Don't filter schools
          onAssignmentUpdate?.(null); // Clear assignment display
          return;
        }
        
        // Clear out of district message if location is in district
        setOutOfDistrictMessage(null);
        
        // Set focused assignment to filter the map
        const focusedSchools = {
          elementary: assignment.zones.elementary?.properties.name,
          middle: assignment.zones.middle?.properties.name,
          high: assignment.zones.high?.properties.name,
        };
        console.log('Setting focused assignment:', focusedSchools);
        onSetFocusedAssignment(focusedSchools);
        
        // Pass assignment data to sidebar and calculate driving times
        if (onAssignmentUpdate) {
          const assignmentData = {
            lat,
            lng,
            address, // Include address from reverse geocoding (may be undefined initially)
            elementary: assignment.zones.elementary?.properties.name,
            middle: assignment.zones.middle?.properties.name,
            high: assignment.zones.high?.properties.name,
          };
          
          // Set the assignment pin immediately for map clicks
          setAssignmentPin({ lat, lng });
          
          // Send assignment data with address (if available)
          onAssignmentUpdate(assignmentData);
          
          // Then calculate driving times asynchronously
          calculateDrivingTimes(lat, lng, assignment).then(drivingTimes => {
            onAssignmentUpdate({
              ...assignmentData,
              drivingTimes
            });
          });
        }
      }
      
      onLocationClick?.(lat, lng);
    },
  });

  // Store map reference and add custom zoom control
  useEffect(() => {
    mapRef.current = map;
    
    // Add custom zoom control positioned at bottom left
    const zoomControl = L.control.zoom({ position: 'bottomleft' });
    map.addControl(zoomControl);
    
    // Cleanup function to remove control when component unmounts
    return () => {
      try {
        if (zoomControl && map) {
          map.removeControl(zoomControl);
        }
      } catch (error) {
        // Silently handle cleanup errors (e.g., if map is already destroyed)
        console.warn('Error removing zoom control:', error);
      }
    };
  }, [map, mapRef]);

  // Auto-fit bounds when zones are loaded
  useEffect(() => {
    if (zones && mapRef.current && !searchMarker) {
      const bounds = calculateBounds(zones);
      if (bounds) {
        console.log('Fitting map to calculated bounds:', bounds);
        // Add a small delay to ensure map is fully initialized
        setTimeout(() => {
          if (mapRef.current && mapRef.current.getContainer()) {
            try {
              mapRef.current.fitBounds(bounds, getFitBoundsOptions());
            } catch (error) {
              console.warn('Error fitting bounds:', error);
            }
          }
        }, 100);
      }
    }
  }, [zones, searchMarker, mapRef]);

  // Zoom to search marker when it changes
  useEffect(() => {
    if (searchMarker && mapRef.current) {
      // Add a small delay to ensure map is ready
      setTimeout(() => {
        if (mapRef.current && mapRef.current.getContainer()) {
          try {
            mapRef.current.setView([searchMarker.lat, searchMarker.lng], 15);
            
            // Show school assignment for the searched location
            if (zones) {
              const assignment = findSchoolAssignment(searchMarker.lat, searchMarker.lng, zones);
              
              // Check if location is in Frisco ISD
              const hasAnyZone = assignment.zones.elementary || assignment.zones.middle || assignment.zones.high;
              
              if (!hasAnyZone) {
                // Location is not in Frisco ISD
                console.log('Searched address not in Frisco ISD');
                setOutOfDistrictMessage({ lat: searchMarker.lat, lng: searchMarker.lng, address: searchMarker.address });
                onSetFocusedAssignment(null); // Don't filter schools
                onAssignmentUpdate?.(null); // Clear assignment display
                
                // Clear search marker after showing message
                setTimeout(() => {
                  onClearSearchMarker();
                }, 500);
                return;
              }
              
              // Clear out of district message if location is in district
              setOutOfDistrictMessage(null);
              
              // Set focused assignment for address search too
              const focusedSchools = {
                elementary: assignment.zones.elementary?.properties.name,
                middle: assignment.zones.middle?.properties.name,
                high: assignment.zones.high?.properties.name,
              };
              onSetFocusedAssignment(focusedSchools);
              
              // Pass assignment data to sidebar and calculate driving times
              if (onAssignmentUpdate) {
                const assignmentData = {
                  lat: searchMarker.lat,
                  lng: searchMarker.lng,
                  address: searchMarker.address,
                  elementary: assignment.zones.elementary?.properties.name,
                  middle: assignment.zones.middle?.properties.name,
                  high: assignment.zones.high?.properties.name,
                };
                
                // Set the assignment pin immediately for address searches
                setAssignmentPin({ lat: searchMarker.lat, lng: searchMarker.lng });
                
                // Send assignment data
                onAssignmentUpdate(assignmentData);
                
                // Then calculate driving times asynchronously
                calculateDrivingTimes(searchMarker.lat, searchMarker.lng, assignment).then(drivingTimes => {
                  onAssignmentUpdate({
                    ...assignmentData,
                    drivingTimes
                  });
                });
              }
              
              // Clear search marker to match map click behavior - only show assignment pin
              setTimeout(() => {
                onClearSearchMarker();
              }, 500);
            }
          } catch (error) {
            console.warn('Error setting search marker view:', error);
          }
        }
      }, 100);
    }
  }, [searchMarker, zones, mapRef, onSetFocusedAssignment, onClearSearchMarker, onAssignmentUpdate]);

  return null;
}

export default function Map({ 
  onLocationClick, 
  filters, 
  searchMarker, 
  onSchoolCountUpdate,
  onClearSearchMarker,
  onAssignmentUpdate
}: MapComponentProps) {
  const [zones, setZones] = useState<ZoneCollection | null>(null);
  const [schools, setSchools] = useState<SchoolCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focusedAssignment, setFocusedAssignment] = useState<{
    elementary?: string;
    middle?: string;
    high?: string;
  } | null>(null);
  const [assignmentPin, setAssignmentPin] = useState<{lat: number; lng: number} | null>(null);
  const [currentAssignment, setCurrentAssignment] = useState<{
    lat: number;
    lng: number;
    address?: string;
    elementary?: string;
    middle?: string;
    high?: string;
    drivingTimes?: Array<{
      schoolName: string;
      durationText: string;
      distanceText: string;
      isEstimate?: boolean;
    }>;
  } | null>(null);
  const [highlightedZones, setHighlightedZones] = useState<{
    elementary?: string;
    middle?: string;
    high?: string;
  } | null>(null);
  const [outOfDistrictMessage, setOutOfDistrictMessage] = useState<{lat: number; lng: number; address?: string} | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const data = await loadAllData();
        setZones(data.zones);
        setSchools(data.schools);
        console.log('Loaded schools:', data.schools.features.length);
        console.log('Loaded zones:', data.zones.features.length);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load map data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Update school counts when data loads
  useEffect(() => {
    if (schools) {
      const counts = {
        elementary: schools.features.filter(s => s.properties.type === 'Elementary').length,
        middle: schools.features.filter(s => s.properties.type === 'Middle School').length,
        high: schools.features.filter(s => s.properties.type === 'High School').length,
        total: schools.features.length
      };
      onSchoolCountUpdate(counts);
    }
  }, [schools, onSchoolCountUpdate]);

  // Filter schools and zones based on current filters
  const filteredSchools = useMemo(() => {
    if (!schools) return null;

    return {
      ...schools,
      features: schools.features.filter(school => {
        // Filter by school type (only applies when not in focused mode)
        if (!focusedAssignment) {
          const typeVisible = 
            (school.properties.type === 'Elementary' && filters.showElementary) ||
            (school.properties.type === 'Middle School' && filters.showMiddle) ||
            (school.properties.type === 'High School' && filters.showHigh);

          if (!typeVisible) return false;
        }

        // Filter by search term
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          return school.properties.name.toLowerCase().includes(searchLower);
        }

        return true;
      })
    };
  }, [schools, filters, focusedAssignment]);

  // Separate zones by type for proper layer ordering
  const filteredZonesByType = useMemo(() => {
    if (!zones) return { high: null, middle: null, elementary: null };

    const filterZone = (zone: ZoneFeature) => {
      // Filter by school type (only applies when not in focused mode)
      if (!focusedAssignment) {
        const typeVisible = 
          (zone.properties.type === 'Elementary' && filters.showElementary) ||
          (zone.properties.type === 'Middle School' && filters.showMiddle) ||
          (zone.properties.type === 'High School' && filters.showHigh);

        if (!typeVisible) return false;
      }

      // Filter by search term
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        return zone.properties.name.toLowerCase().includes(searchLower);
      }

      return true;
    };

    const highSchoolZones = zones.features.filter(zone => 
      zone.properties.type === 'High School' && filterZone(zone)
    );
    const middleSchoolZones = zones.features.filter(zone => 
      zone.properties.type === 'Middle School' && filterZone(zone)
    );
    const elementaryZones = zones.features.filter(zone => 
      zone.properties.type === 'Elementary' && filterZone(zone)
    );

    return {
      high: highSchoolZones.length > 0 ? { ...zones, features: highSchoolZones } : null,
      middle: middleSchoolZones.length > 0 ? { ...zones, features: middleSchoolZones } : null,
      elementary: elementaryZones.length > 0 ? { ...zones, features: elementaryZones } : null,
    };
  }, [zones, filters, focusedAssignment]);

  // Zone styling function with highlight for search matches and hover highlighting
  const getZoneStyle = (feature: ZoneFeature) => {
    const isSearchMatch = filters.searchTerm && 
      feature.properties.name.toLowerCase().includes(filters.searchTerm.toLowerCase());
    
    const isHighlighted = highlightedZones && (
      (feature.properties.type === 'Elementary' && highlightedZones.elementary === feature.properties.name) ||
      (feature.properties.type === 'Middle School' && highlightedZones.middle === feature.properties.name) ||
      (feature.properties.type === 'High School' && highlightedZones.high === feature.properties.name)
    );

    // Check if this zone is part of the focused assignment
    const isAssigned = focusedAssignment && (
      (feature.properties.type === 'Elementary' && focusedAssignment.elementary === feature.properties.name) ||
      (feature.properties.type === 'Middle School' && focusedAssignment.middle === feature.properties.name) ||
      (feature.properties.type === 'High School' && focusedAssignment.high === feature.properties.name)
    );

    if (isHighlighted) {
      return {
        fillColor: feature.properties.color,
        weight: 4,
        opacity: 1,
        color: feature.properties.color,
        dashArray: '',
        fillOpacity: 0.4,
      };
    }

    // When in focused mode, make non-assigned zones more transparent
    const isInFocusedMode = !!focusedAssignment;
    const baseOpacity = isInFocusedMode && !isAssigned ? 0.3 : 1;
    const baseFillOpacity = isInFocusedMode && !isAssigned ? 0.05 : (isSearchMatch ? 0.3 : 0.1);

    return {
      fillColor: feature.properties.color,
      weight: isSearchMatch ? 3 : 2,
      opacity: baseOpacity,
      color: isSearchMatch ? '#FF0000' : feature.properties.color,
      dashArray: isSearchMatch ? '5' : '3',
      fillOpacity: baseFillOpacity,
    };
  };

  const onZoneMouseOver = (e: L.LeafletMouseEvent) => {
    if (!zones) return;
    
    // Use the actual mouse position to find school assignments
    const { lat, lng } = e.latlng;
    const assignment = findSchoolAssignment(lat, lng, zones);
    
    // Set highlighted zones to include all three zones that serve this area
    setHighlightedZones({
      elementary: assignment.zones.elementary?.properties.name,
      middle: assignment.zones.middle?.properties.name,
      high: assignment.zones.high?.properties.name,
    });
  };

  const onZoneMouseOut = () => {
    // Clear all highlighting when mouse leaves any zone
    setHighlightedZones(null);
  };

  // Set up global function for clearing focused view (used by popup buttons)
  useEffect(() => {
    (window as any).clearFocusedView = () => {
      setFocusedAssignment(null);
      setAssignmentPin(null);
    };
    
    return () => {
      delete (window as any).clearFocusedView;
    };
  }, []);

  // Helper function to handle assignment updates with driving times (used by zone clicks)
  const handleAssignmentWithDrivingTimes = async (lat: number, lng: number, assignment: any) => {
    const assignmentData = {
      lat,
      lng,
      elementary: assignment.zones.elementary?.properties.name,
      middle: assignment.zones.middle?.properties.name,
      high: assignment.zones.high?.properties.name,
    };
    
    // Set the assignment pin immediately for zone clicks
    setAssignmentPin({ lat, lng });
    
    // Send assignment data
    handleAssignmentUpdate(assignmentData);
    
    // Calculate driving times asynchronously
    if (schools) {
      try {
        const schoolCoords = getAssignedSchoolCoordinates(assignment, schools);
        if (schoolCoords.length > 0) {
          const routes = await calculateMultipleRoutes(lat, lng, schoolCoords);
          const drivingTimes = routes.map(({ name, route }) => {
            if (route) {
              return {
                schoolName: name,
                durationText: route.durationText,
                distanceText: route.distanceText,
                isEstimate: false
              };
            } else {
              const schoolCoord = schoolCoords.find(s => s.name === name);
              if (schoolCoord) {
                const estimate = estimateDrivingTime(lat, lng, schoolCoord.lat, schoolCoord.lng);
                return {
                  schoolName: name,
                  durationText: estimate.durationText,
                  distanceText: estimate.distanceText,
                  isEstimate: true
                };
              }
              return null;
            }
          }).filter((item): item is {schoolName: string; durationText: string; distanceText: string; isEstimate: boolean} => item !== null);
          
          handleAssignmentUpdate({
            ...assignmentData,
            drivingTimes
          });
        }
      } catch (error) {
        console.error('Error calculating driving times:', error);
      }
    }
  };

  // Handle assignment updates from map clicks
  const handleAssignmentUpdate = (assignment: {lat: number; lng: number; address?: string; elementary?: string; middle?: string; high?: string; drivingTimes?: Array<{schoolName: string; durationText: string; distanceText: string; isEstimate?: boolean;}>;} | null) => {
    if (assignment) {
      // Store the current assignment data for the popup
      setCurrentAssignment(assignment);
      // Clear out-of-district message when we have a valid assignment
      setOutOfDistrictMessage(null);
    } else {
      setAssignmentPin(null);
      setCurrentAssignment(null);
    }
    onAssignmentUpdate?.(assignment);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading school zone map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center p-6 bg-white rounded-lg shadow-lg max-w-md">
          <div className="text-red-500 text-xl mb-2">⚠️ Error</div>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full relative">
      <MapContainer
        center={FALLBACK_CENTER}
        zoom={FALLBACK_ZOOM}
        className="h-full w-full"
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapController 
          onLocationClick={onLocationClick} 
          mapRef={mapRef} 
          zones={zones}
          schools={schools}
          searchMarker={searchMarker}
          onClearSearchMarker={onClearSearchMarker}
          onSetFocusedAssignment={setFocusedAssignment}
          onAssignmentUpdate={handleAssignmentUpdate}
          setOutOfDistrictMessage={setOutOfDistrictMessage}
          setAssignmentPin={setAssignmentPin}
        />
        
        {/* Render zone boundaries in proper layer order: High School (bottom), Middle School (middle), Elementary (top) */}
        
        {/* High School zones - bottom layer */}
        {filteredZonesByType.high && (
          <GeoJSON
            key={`high-zones-${JSON.stringify(filters)}-${JSON.stringify(focusedAssignment)}-${JSON.stringify(highlightedZones)}`}
            data={filteredZonesByType.high}
            style={(feature) => getZoneStyle(feature as ZoneFeature)}
            onEachFeature={(feature, layer) => {
              const zoneFeature = feature as ZoneFeature;
              
              // Add event listeners
              layer.on({
                click: (e) => {
                  // Get the click location
                  const { lat, lng } = e.latlng;
                  
                  // Find all school assignments for this location
                  const assignment = findSchoolAssignment(lat, lng, zones!);
                  
                  // Set focused assignment to filter the map
                  const focusedSchools = {
                    elementary: assignment.zones.elementary?.properties.name,
                    middle: assignment.zones.middle?.properties.name,
                    high: assignment.zones.high?.properties.name,
                  };
                  console.log('Zone click - Setting focused assignment:', focusedSchools);
                  setFocusedAssignment(focusedSchools);
                  
                  // Handle assignment with driving times
                  handleAssignmentWithDrivingTimes(lat, lng, assignment);
                },
                mouseover: onZoneMouseOver,
                mouseout: onZoneMouseOut,
              });
            }}
          />
        )}

        {/* Middle School zones - middle layer */}
        {filteredZonesByType.middle && (
          <GeoJSON
            key={`middle-zones-${JSON.stringify(filters)}-${JSON.stringify(focusedAssignment)}-${JSON.stringify(highlightedZones)}`}
            data={filteredZonesByType.middle}
            style={(feature) => getZoneStyle(feature as ZoneFeature)}
            onEachFeature={(feature, layer) => {
              const zoneFeature = feature as ZoneFeature;
              
              // Add event listeners
              layer.on({
                click: (e) => {
                  // Get the click location
                  const { lat, lng } = e.latlng;
                  
                  // Find all school assignments for this location
                  const assignment = findSchoolAssignment(lat, lng, zones!);
                  
                  // Set focused assignment to filter the map
                  const focusedSchools = {
                    elementary: assignment.zones.elementary?.properties.name,
                    middle: assignment.zones.middle?.properties.name,
                    high: assignment.zones.high?.properties.name,
                  };
                  console.log('Zone click - Setting focused assignment:', focusedSchools);
                  setFocusedAssignment(focusedSchools);
                  
                  // Handle assignment with driving times
                  handleAssignmentWithDrivingTimes(lat, lng, assignment);
                },
                mouseover: onZoneMouseOver,
                mouseout: onZoneMouseOut,
              });
            }}
          />
        )}

        {/* Elementary School zones - top layer */}
        {filteredZonesByType.elementary && (
          <GeoJSON
            key={`elementary-zones-${JSON.stringify(filters)}-${JSON.stringify(focusedAssignment)}-${JSON.stringify(highlightedZones)}`}
            data={filteredZonesByType.elementary}
            style={(feature) => getZoneStyle(feature as ZoneFeature)}
            onEachFeature={(feature, layer) => {
              const zoneFeature = feature as ZoneFeature;
              
              // Add event listeners
              layer.on({
                click: (e) => {
                  // Get the click location
                  const { lat, lng } = e.latlng;
                  
                  // Find all school assignments for this location
                  const assignment = findSchoolAssignment(lat, lng, zones!);
                  
                  // Set focused assignment to filter the map
                  const focusedSchools = {
                    elementary: assignment.zones.elementary?.properties.name,
                    middle: assignment.zones.middle?.properties.name,
                    high: assignment.zones.high?.properties.name,
                  };
                  console.log('Zone click - Setting focused assignment:', focusedSchools);
                  setFocusedAssignment(focusedSchools);
                  
                  // Handle assignment with driving times
                  handleAssignmentWithDrivingTimes(lat, lng, assignment);
                },
                mouseover: onZoneMouseOver,
                mouseout: onZoneMouseOut,
              });
            }}
          />
        )}

        {/* Render filtered school markers */}
        {filteredSchools && filteredSchools.features.map((school: SchoolFeature) => {
          const isSearchMatch = filters.searchTerm && 
            school.properties.name.toLowerCase().includes(filters.searchTerm.toLowerCase());

          // Check if this school should be highlighted (is part of current hover)
          const isHighlighted = highlightedZones && (
            (school.properties.type === 'Elementary' && highlightedZones.elementary === school.properties.name) ||
            (school.properties.type === 'Middle School' && highlightedZones.middle === school.properties.name) ||
            (school.properties.type === 'High School' && highlightedZones.high === school.properties.name)
          );

          // Check if this school is part of the focused assignment
          const isAssigned = focusedAssignment && (
            (school.properties.type === 'Elementary' && focusedAssignment.elementary === school.properties.name) ||
            (school.properties.type === 'Middle School' && focusedAssignment.middle === school.properties.name) ||
            (school.properties.type === 'High School' && focusedAssignment.high === school.properties.name)
          );

          // Determine opacity
          let opacity = 1;
          if (highlightedZones && !isHighlighted) {
            opacity = 0.5; // Reduced when hovering zones and this isn't highlighted
          } else if (focusedAssignment && !isAssigned) {
            opacity = 0.3; // Reduced when in focused mode and this isn't assigned
          }

          // Create a custom colored marker that matches the zone color
          const markerColor = isSearchMatch ? '#DC2626' : school.properties.color;
          const schoolIcon = new L.DivIcon({
            className: 'school-marker',
            html: `
              <div style="
                width: 20px;
                height: 20px;
                background: ${markerColor};
                border: 2px solid white;
                border-radius: 50%;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                font-weight: bold;
                color: white;
                opacity: ${opacity};
              ">
                ${school.properties.type === 'Elementary' ? 'E' : school.properties.type === 'Middle School' ? 'M' : 'H'}
              </div>
            `,
            iconSize: [20, 20],
            iconAnchor: [10, 10] // Center the icon
          });

          // Create a custom label icon positioned to the right of the marker
          const labelIcon = new L.DivIcon({
            className: 'school-label',
            html: `
              <div style="
                font-size: 12px; 
                font-weight: bold; 
                color: ${markerColor};
                text-shadow: 1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8), 1px -1px 2px rgba(255,255,255,0.8), -1px 1px 2px rgba(255,255,255,0.8);
                white-space: nowrap;
                pointer-events: none;
                opacity: ${opacity};
              ">
                ${school.properties.name}${isSearchMatch ? ' 🎯' : ''}
              </div>
            `,
            iconSize: [0, 0], // Will auto-size based on content
            iconAnchor: [-15, 9] // Position just to the right with no vertical offset
          });

          return (
            <React.Fragment key={`${school.properties.name}-${JSON.stringify(focusedAssignment)}-${JSON.stringify(highlightedZones)}`}>
              {/* School marker */}
              <Marker
                position={[
                  school.geometry.coordinates[1], // latitude
                  school.geometry.coordinates[0]  // longitude
                ]}
                icon={schoolIcon}
                eventHandlers={{
                  // Prevent marker hover from clearing zone highlights
                  mouseover: (e) => {
                    // Don't call onZoneMouseOut - keep zone hover active
                    e.originalEvent.stopPropagation();
                  },
                  mouseout: (e) => {
                    // Don't call onZoneMouseOut - let zone handle its own mouseout
                    e.originalEvent.stopPropagation();
                  }
                }}
              >
                <Popup>
                  <div className="p-2">
                    <h3 
                      className={`font-bold text-lg ${isSearchMatch ? 'text-red-600' : ''}`} 
                      style={{ color: markerColor }}
                    >
                      {school.properties.name}
                      {isSearchMatch && ' 🎯'}
                    </h3>
                    <p className="text-sm text-gray-600">{school.properties.type} School</p>
                    <p className="text-sm">{school.properties.grades}</p>
                    <p className="text-sm">{school.properties.address}</p>
                    {school.properties.url && (
                      <a 
                        href={school.properties.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Visit School Website
                      </a>
                    )}
                  </div>
                </Popup>
              </Marker>
              
              {/* School label positioned using pixel offset */}
              <Marker
                position={[
                  school.geometry.coordinates[1], // Same position as marker
                  school.geometry.coordinates[0]  // Same position as marker
                ]}
                icon={labelIcon}
                eventHandlers={{
                  // Prevent label hover from clearing zone highlights
                  mouseover: (e) => {
                    // Don't call onZoneMouseOut - keep zone hover active
                    e.originalEvent.stopPropagation();
                  },
                  mouseout: (e) => {
                    // Don't call onZoneMouseOut - let zone handle its own mouseout
                    e.originalEvent.stopPropagation();
                  }
                }}
              />
            </React.Fragment>
          );
        })}

        {/* Render search marker */}
        {searchMarker && (
          <Marker
            position={[searchMarker.lat, searchMarker.lng]}
            icon={searchIcon}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-bold text-lg text-red-600">📍 Search Result</h3>
                <p className="text-sm">{searchMarker.address}</p>
                <button
                  onClick={onClearSearchMarker}
                  className="mt-2 text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                >
                  Clear
                </button>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Render assignment pin marker */}
        {assignmentPin && (
          <Marker
            position={[assignmentPin.lat, assignmentPin.lng]}
            icon={locationIcon}
          >
            <Popup>
              <div className="p-3 min-w-64">
                <h3 className="font-bold text-lg text-blue-600 mb-2">📍 School Assignments</h3>
                
                {currentAssignment?.address && (
                  <p className="text-sm text-gray-600 mb-3">{currentAssignment.address}</p>
                )}
                
                {/* Elementary School */}
                {currentAssignment?.elementary && (
                  <div className="mb-3">
                    <h4 className="font-semibold text-blue-700 text-sm mb-1">🔵 Elementary School</h4>
                    <p className="text-sm font-medium">{currentAssignment.elementary}</p>
                    {currentAssignment.drivingTimes?.find(dt => dt.schoolName === currentAssignment.elementary) && (
                      <div className="flex items-center text-xs text-gray-600 mt-1">
                        <span className="mr-1">🚗</span>
                        <span>
                          {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.elementary)?.durationText}
                          {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.elementary)?.isEstimate && ' (est.)'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Middle School */}
                {currentAssignment?.middle && (
                  <div className="mb-3">
                    <h4 className="font-semibold text-green-700 text-sm mb-1">🟢 Middle School</h4>
                    <p className="text-sm font-medium">{currentAssignment.middle}</p>
                    {currentAssignment.drivingTimes?.find(dt => dt.schoolName === currentAssignment.middle) && (
                      <div className="flex items-center text-xs text-gray-600 mt-1">
                        <span className="mr-1">🚗</span>
                        <span>
                          {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.middle)?.durationText}
                          {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.middle)?.isEstimate && ' (est.)'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* High School */}
                {currentAssignment?.high && (
                  <div className="mb-3">
                    <h4 className="font-semibold text-orange-700 text-sm mb-1">🟠 High School</h4>
                    <p className="text-sm font-medium">{currentAssignment.high}</p>
                    {currentAssignment.drivingTimes?.find(dt => dt.schoolName === currentAssignment.high) && (
                      <div className="flex items-center text-xs text-gray-600 mt-1">
                        <span className="mr-1">🚗</span>
                        <span>
                          {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.high)?.durationText}
                          {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.high)?.isEstimate && ' (est.)'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {!currentAssignment?.elementary && !currentAssignment?.middle && !currentAssignment?.high && (
                  <p className="text-sm text-gray-500 mb-3">Loading school assignment information...</p>
                )}
                
                <button
                  onClick={() => {
                    setFocusedAssignment(null);
                    setAssignmentPin(null);
                    setCurrentAssignment(null);
                    onAssignmentUpdate?.(null);
                  }}
                  className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                >
                  Clear Assignment
                </button>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Render out-of-district marker */}
        {outOfDistrictMessage && (
          <Marker
            position={[outOfDistrictMessage.lat, outOfDistrictMessage.lng]}
            icon={locationIcon}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-bold text-lg text-red-600">⚠️ Outside Frisco ISD</h3>
                <p className="text-sm text-red-700">This location is not within the Frisco ISD boundaries.</p>
                {outOfDistrictMessage.address && (
                  <p className="text-xs text-gray-600 mt-1">{outOfDistrictMessage.address}</p>
                )}
                <p className="text-xs text-gray-600 mt-2">All schools are shown on the map (no filtering applied).</p>
                <button
                  onClick={() => {
                    setOutOfDistrictMessage(null);
                  }}
                  className="mt-2 text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                >
                  Clear
                </button>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Filter indicator */}
      {(focusedAssignment || outOfDistrictMessage || filters.searchTerm || !filters.showElementary || !filters.showMiddle || !filters.showHigh) && (
        <div className="absolute top-4 right-4 bg-white shadow-lg rounded-lg p-3 z-10">
          <h4 className="font-semibold text-sm text-gray-700 mb-2">Active Filters:</h4>
          
          {outOfDistrictMessage && (
            <div className="mb-2 p-2 bg-red-50 rounded border border-red-200">
              <p className="text-xs font-semibold text-red-700 mb-1">⚠️ Outside Frisco ISD</p>
              <p className="text-xs text-red-600">Location not in district boundaries</p>
              {outOfDistrictMessage.address && (
                <p className="text-xs text-gray-600 mt-1">{outOfDistrictMessage.address}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">All schools shown (no filtering)</p>
              <button
                onClick={() => setOutOfDistrictMessage(null)}
                className="mt-1 text-xs bg-red-100 hover:bg-red-200 px-2 py-1 rounded text-red-700"
              >
                Clear
              </button>
            </div>
          )}
          
          {focusedAssignment && (
            <div className="mb-2 p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs font-semibold text-blue-700 mb-1">🎯 Focused Assignment View</p>
              {focusedAssignment.elementary && (
                <p className="text-xs text-blue-600">🔵 Elementary: {focusedAssignment.elementary}</p>
              )}
              {focusedAssignment.middle && (
                <p className="text-xs text-blue-600">🟢 Middle: {focusedAssignment.middle}</p>
              )}
              {focusedAssignment.high && (
                <p className="text-xs text-blue-600">🟠 High: {focusedAssignment.high}</p>
              )}
              <button
                onClick={() => {
                  setFocusedAssignment(null);
                  setAssignmentPin(null);
                  setCurrentAssignment(null);
                  onAssignmentUpdate?.(null);
                }}
                className="mt-1 text-xs bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded text-blue-700"
              >
                Show All Schools
              </button>
            </div>
          )}
          
          {filters.searchTerm && (
            <p className="text-xs text-blue-600">🔍 Search: "{filters.searchTerm}"</p>
          )}
          {(!filters.showElementary || !filters.showMiddle || !filters.showHigh) && !focusedAssignment && !outOfDistrictMessage && (
            <p className="text-xs text-gray-600">
              Showing: {[
                filters.showElementary && 'Elementary',
                filters.showMiddle && 'Middle', 
                filters.showHigh && 'High'
              ].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}